///
/// EarthBrowser
///
/// Created by Matt Giger on 9/17/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Info panel for a focused user placemark. The point of a placemark is
/// that the globe under it is alive: the panel samples current conditions
/// at the anchor (weather grid) and rolls up active alerts across every
/// member location from the local weather_alerts table ("2 of 14 locations
/// under Severe Thunderstorm Warning"). Members list their own alert state
/// and fly the camera when clicked.
///

import { cSeverityBadge } from "@/components/controls/anchored-panel/AnchoredAlertInfoPanel"
import { getWeatherIcon } from "@/components/controls/anchored-panel/AnchoredLocationForecastPanel"
import { AnchoredPanelShell, type PanelShellContext } from "@/components/controls/anchored-panel/AnchoredPanelShell"
import { Badge, InfoField, MoreSection } from "@/components/controls/anchored-panel/panelAtoms"
import { getActiveAlertsForLocations, type AlertHit } from "@/earthbrowser/dataset-distribution/alerts/alertsAtPoint"
import { type AlertSeverity } from "@/earthbrowser/dataset-distribution/alerts/AlertsTable"
import { useCurrentWeatherGrid } from "@/earthbrowser/dataset-distribution/weather-grid"
import { convertTemperature, formatSpeed } from "@/earthbrowser/utils/unitFormatters"
import { useToast } from "@/components/ui/use-toast"
import { useEntitlements } from "@/hooks/useEntitlements"
import { usePushChannel } from "@/hooks/usePushChannel"
import { setSubscribeOpen } from "@/redux/dialogSlice"
import { sendCameraCommand, sendDatasetAction } from "@/redux/earthBrowserSlice"
import { useListPlacemarkAlertsQuery, usePutPlacemarkAlertMutation } from "@/redux/placemarkAlertsApi"
import { cPlacemarkColors, editLocation, placemarkAnchor, type Placemark, type PlacemarkLocation } from "@/redux/placemarksSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { Bell, BellRing, Pencil } from "lucide-react"
import { useEffect, useRef, useState } from "react"

const cSeverityDot: Record<AlertSeverity, string> = {
	Extreme: "bg-fuchsia-400",
	Severe: "bg-red-400",
	Moderate: "bg-orange-400",
	Minor: "bg-yellow-300",
	Unknown: "bg-slate-400",
}

/** Most alert rows shown before the rest collapse into a "+N more" line */
const cMaxAlertRows = 3

/** "37.7749°N, 122.4194°W" */
function formatCoords(lat: number, lon: number): string {
	const latLabel = `${Math.abs(lat).toFixed(4)}°${lat >= 0 ? "N" : "S"}`
	const lonLabel = `${Math.abs(lon).toFixed(4)}°${lon >= 0 ? "E" : "W"}`
	return `${latLabel}, ${lonLabel}`
}

/**
 * Active alerts covering the placemark's members, re-queried when the
 * membership changes. Local-table read only; [] when alerts haven't synced.
 */
function usePlacemarkAlerts(placemark: Placemark): AlertHit[] {
	const [hits, setHits] = useState<AlertHit[]>([])
	const requestRef = useRef(0)

	useEffect(() => {
		const requestId = ++requestRef.current
		getActiveAlertsForLocations(placemark.locations)
			.then(result => {
				if (requestRef.current === requestId) setHits(result)
			})
			.catch(() => {
				if (requestRef.current === requestId) setHits([])
			})
	}, [placemark.id, placemark.locations])

	return hits
}

export function AnchoredPlacemarkInfoPanel() {
	return (
		<AnchoredPanelShell
			match={data => data?.type === "placemark" ? data : null}
			fallbackTitle={content => content.placemark.name}
			accentBorder="border-white/15"
			pill={content => (
				<div
					className="h-[3px] w-11 rounded-full"
					style={{ backgroundColor: cPlacemarkColors[content.placemark.color] ?? cPlacemarkColors.red }}
				/>
			)}
			contentKey={content => content.memberId
				? `${content.placemark.id}:${content.memberId}`
				: content.placemark.id}
		>
			{(content, ctx) => content.memberId
				? <MemberBody placemark={content.placemark} memberId={content.memberId} ctx={ctx} />
				: <PlacemarkBody placemark={content.placemark} ctx={ctx} />}
		</AnchoredPanelShell>
	)
}

/**
 * Callout for one focused member of an expanded multi-location placemark:
 * its own conditions and alerts, and a breadcrumb back to the whole place.
 */
function MemberBody({ placemark, memberId, ctx }: { placemark: Placemark; memberId: string; ctx: PanelShellContext }) {
	const dispatch = useAppDispatch()
	const alertHits = usePlacemarkAlerts(placemark)
	const member = placemark.locations.find(location => location.id === memberId)
	if (!member) return null

	const memberAlerts = alertHits.filter(hit => hit.locationIds.includes(memberId))

	return (
		<>
			<div className="space-y-1">
				<div className="text-xl font-bold text-white leading-tight">
					{ctx.title}
				</div>
				<div className="text-sm text-slate-300">
					{formatCoords(member.lat, member.lon)}
				</div>
			</div>

			{memberAlerts.length > 0 && (
				<AlertRollup hits={memberAlerts} memberCount={1} />
			)}

			<ConditionsRow lat={member.lat} lon={member.lon} />

			<MemberNote placemarkId={placemark.id} member={member} />

			<button
				type="button"
				onClick={() => {
					// Release the member focus first so refocusing the parent is
					// a fresh focus request (re-frames even when the anchor node
					// is what's technically focused, e.g. member deep links)
					dispatch(sendDatasetAction({ type: "", action: "deselect" }))
					dispatch(sendDatasetAction({
						type: "placemark",
						action: "selectId",
						id: placemark.id,
					}))
				}}
				className="pointer-events-auto flex items-center gap-1 text-xs text-slate-300 hover:text-white cursor-pointer"
				aria-label={`Back to ${placemark.name}`}
			>
				<span aria-hidden>←</span>
				<span className="truncate">
					{placemark.name} ({placemark.locations.length} locations)
				</span>
			</button>
		</>
	)
}

function PlacemarkBody({ placemark, ctx }: { placemark: Placemark; ctx: PanelShellContext }) {
	const anchor = placemarkAnchor(placemark)
	const members = placemark.locations
	const alertHits = usePlacemarkAlerts(placemark)

	// Member id → most severe alert covering it (member list indicators)
	const memberSeverity = new Map<string, AlertSeverity>()
	for (const hit of alertHits) {
		for (const id of hit.locationIds) {
			if (!memberSeverity.has(id)) memberSeverity.set(id, hit.alert.severity)
		}
	}

	return (
		<>
			<div className="space-y-1">
				<div className="text-xl font-bold text-white leading-tight">
					{ctx.title}
				</div>
				<div className="text-sm text-slate-300">
					{formatCoords(anchor.lat, anchor.lon)}
				</div>
			</div>

			{alertHits.length > 0 && (
				<AlertRollup hits={alertHits} memberCount={members.length} />
			)}

			<ConditionsRow lat={anchor.lat} lon={anchor.lon} />

			<AlertRuleToggle placemarkId={placemark.id} />

			{placemark.note && (
				<div className="text-xs text-slate-300 leading-snug whitespace-pre-line">
					{placemark.note}
				</div>
			)}

			{members.length > 1 && (
				<MoreSection compact={ctx.compact}>
					<div className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 space-y-1.5">
						<div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
							{members.length} locations
						</div>
						{members.map(location => (
							<MemberRow
								key={location.id}
								location={location}
								severity={memberSeverity.get(location.id)}
							/>
						))}
					</div>
				</MoreSection>
			)}

			{(placemark.tags.length > 0 || members.length === 1) && (
				<div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
					{anchor.altitudeM !== undefined && (
						<InfoField label="Elevation" value={`${Math.round(anchor.altitudeM)} m`} />
					)}
					{placemark.tags.length > 0 && (
						<InfoField label="Tags" value={placemark.tags.join(", ")} />
					)}
				</div>
			)}
		</>
	)
}

/**
 * "Alert notifications" row (subscription feature): toggles the server-side
 * rule that emails the owner when any member location is newly inside an
 * active severe weather alert. Free accounts get an upsell that opens the
 * subscribe dialog; severity/filter details live in Profile → Alerts.
 */
function AlertRuleToggle({ placemarkId }: { placemarkId: string }) {
	const dispatch = useAppDispatch()
	const signedIn = useAppSelector(state => !!state.user.userProfile)
	const { isPaid } = useEntitlements()
	const { data } = useListPlacemarkAlertsQuery(undefined, { skip: !signedIn || !isPaid })
	const [putRule, { isLoading: saving }] = usePutPlacemarkAlertMutation()
	const { enablePush } = usePushChannel()
	const { toast } = useToast()

	if (!signedIn) return null

	const rule = data?.rules.find(r => r.placemark_id === placemarkId)
	const active = rule?.enabled === true

	const handleToggle = () => {
		if (!isPaid) {
			dispatch(setSubscribeOpen(true))
			return
		}
		if (saving) return
		const enabling = !active
		void putRule({
			placemarkId,
			enabled: enabling,
			min_severity: rule?.min_severity ?? "Severe",
			event_filter: rule?.event_filter ?? null,
		})
		if (enabling) {
			// Push is the delivery channel: subscribe this browser now, while
			// we're still inside the click's user gesture (permission prompt)
			void enablePush().then(status => {
				if (status === "denied") {
					toast({
						title: "Notifications blocked",
						description: "Allow notifications for this site in your browser, or turn on email delivery in Profile → Alerts.",
					})
				} else if (status === "unsupported") {
					toast({
						title: "Push not available in this browser",
						description: "Turn on email delivery in Profile → Alerts to receive these alerts.",
					})
				}
			})
		}
	}

	return (
		<button
			type="button"
			onClick={handleToggle}
			aria-label={active ? "Disable weather alert notifications for this place" : "Enable weather alert notifications for this place"}
			className={`pointer-events-auto flex items-center gap-2 w-full rounded-lg border px-3 py-2 text-left text-xs cursor-pointer transition-colors ${
				active
					? "border-amber-400/40 bg-amber-400/10 text-amber-200 hover:bg-amber-400/15"
					: "border-white/10 bg-white/[0.04] text-slate-300 hover:bg-white/[0.08]"
			}`}
		>
			{active
				? <BellRing className="h-3.5 w-3.5 shrink-0" />
				: <Bell className="h-3.5 w-3.5 shrink-0" />}
			<span className="flex-1">
				{active
					? `Alert notifications on (${rule?.min_severity ?? "Severe"}+)`
					: "Notify me of weather alerts here"}
			</span>
			{!isPaid && (
				<span className="shrink-0 rounded-full bg-gradient-to-r from-blue-600 to-cyan-600 px-2 py-0.5 text-[10px] font-semibold text-white">
					Subscribe
				</span>
			)}
		</button>
	)
}

/** Per-member note: displayed inline, editable in place */
function MemberNote({ placemarkId, member }: { placemarkId: string; member: PlacemarkLocation }) {
	const dispatch = useAppDispatch()
	const [editing, setEditing] = useState(false)
	const [draft, setDraft] = useState(member.note ?? "")

	const save = () => {
		dispatch(editLocation({ id: placemarkId, locationId: member.id, note: draft }))
		setEditing(false)
	}

	if (editing) {
		return (
			<div className="space-y-1">
				<textarea
					value={draft}
					onChange={e => setDraft(e.target.value)}
					onKeyDown={e => {
						if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); save() }
						if (e.key === "Escape") { setEditing(false); setDraft(member.note ?? "") }
					}}
					rows={2}
					maxLength={1024}
					autoFocus
					placeholder="Add a note for this location…"
					className="pointer-events-auto w-full text-xs rounded-md bg-black/40 text-white border border-slate-700 focus:outline-none focus:border-blue-500 px-2 py-1.5 resize-none placeholder:text-white/40"
				/>
				<div className="flex gap-2 justify-end text-[11px]">
					<button
						type="button"
						onClick={() => { setEditing(false); setDraft(member.note ?? "") }}
						className="pointer-events-auto text-slate-400 hover:text-white cursor-pointer"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={save}
						className="pointer-events-auto text-blue-300 hover:text-blue-200 cursor-pointer"
					>
						Save
					</button>
				</div>
			</div>
		)
	}

	return (
		<button
			type="button"
			onClick={() => { setDraft(member.note ?? ""); setEditing(true) }}
			className="pointer-events-auto group/note flex items-start gap-1.5 w-full text-left cursor-pointer"
			aria-label={member.note ? "Edit location note" : "Add location note"}
		>
			{member.note ? (
				<span className="text-xs text-slate-300 leading-snug whitespace-pre-line flex-1">
					{member.note}
				</span>
			) : (
				<span className="text-xs text-slate-500 group-hover/note:text-slate-300 flex-1">
					Add a note…
				</span>
			)}
			<Pencil className="w-3 h-3 mt-0.5 shrink-0 text-slate-500 opacity-0 group-hover/note:opacity-100 transition-opacity" />
		</button>
	)
}

/**
 * Active alert roll-up: one row per alert (most severe first), with the
 * member coverage ("2 of 14 locations") when the placemark has more than
 * one member.
 */
function AlertRollup({ hits, memberCount }: { hits: AlertHit[]; memberCount: number }) {
	const shown = hits.slice(0, cMaxAlertRows)
	const hiddenCount = hits.length - shown.length

	return (
		<div className="rounded-md border border-white/10 bg-white/[0.04] px-2 py-1.5 space-y-1">
			{shown.map(({ alert, locationIds }) => {
				const severity: AlertSeverity = alert.severity in cSeverityBadge ? alert.severity : "Unknown"
				return (
					<div key={alert.id} className="flex items-center gap-2 text-xs">
						<Badge colorClass={cSeverityBadge[severity]}>{severity}</Badge>
						<span className="text-slate-100 truncate flex-1">{alert.event}</span>
						{memberCount > 1 && (
							<span className="text-slate-400 shrink-0">
								{locationIds.length} of {memberCount}
							</span>
						)}
					</div>
				)
			})}
			{hiddenCount > 0 && (
				<div className="text-[11px] text-slate-400">
					+{hiddenCount} more active {hiddenCount === 1 ? "alert" : "alerts"}
				</div>
			)}
		</div>
	)
}

/** Current conditions at the anchor, sampled from the weather grid */
function ConditionsRow({ lat, lon }: { lat: number; lon: number }) {
	const { data, isLoading, isError } = useCurrentWeatherGrid(lat, lon)
	const current = data?.current

	if (isError) return null
	if (isLoading || !current) {
		return <div className="text-xs text-slate-500">Loading conditions...</div>
	}

	return (
		<div className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.04] px-2 py-1.5">
			{current.weather?.code && (
				<img
					src={`/images/weather/${getWeatherIcon(current.weather.code)}.png`}
					alt={current.weather.description ?? ""}
					className="w-7 h-7"
				/>
			)}
			<div className="text-lg leading-none font-light text-white">
				{current.temperature?.value != null
					? `${Math.round(convertTemperature(current.temperature.value, 0))}°` : "--°"}
			</div>
			<div className="text-xs text-slate-300 flex-1 truncate">
				{current.weather?.description ?? "--"}
			</div>
			<div className="text-[11px] text-slate-400 shrink-0">
				{current.wind?.speed != null
					? `${formatSpeed(current.wind.speed)} ${current.wind.cardinal ?? ""}` : ""}
			</div>
		</div>
	)
}

/** One member: alert indicator, name, coordinates; click flies the camera there */
function MemberRow({ location, severity }: { location: PlacemarkLocation; severity?: AlertSeverity }) {
	const dispatch = useAppDispatch()

	return (
		<button
			type="button"
			onClick={() => {
				// Release the placemark selection first: flying beyond the framed
				// extent auto-dismisses it anyway, and the release transition
				// would otherwise take over the camera and cancel this flight
				dispatch(sendDatasetAction({ type: "", action: "deselect" }))
				dispatch(sendCameraCommand({
					command: "flyTo/location",
					data: { lat: location.lat, lon: location.lon },
				}))
			}}
			className="pointer-events-auto flex w-full items-baseline justify-between gap-2 text-xs cursor-pointer text-left hover:bg-white/[0.05] rounded px-1 -mx-1"
			aria-label={`Fly to ${location.name ?? formatCoords(location.lat, location.lon)}`}
		>
			<span className="flex items-center gap-1.5 text-slate-200 truncate">
				{severity && (
					<span
						className={`w-1.5 h-1.5 rounded-full shrink-0 ${cSeverityDot[severity] ?? cSeverityDot.Unknown}`}
						title={`Under active ${severity.toLowerCase()} alert`}
					/>
				)}
				{location.name ?? formatCoords(location.lat, location.lon)}
			</span>
			{location.name && (
				<span className="text-slate-500 shrink-0">
					{formatCoords(location.lat, location.lon)}
				</span>
			)}
		</button>
	)
}
